import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { fetchAdminFormateurActivities } from '../../../services/academiaAdminService';
import { ADMIN_TABLE_PAGE_SIZE } from '../../../hooks/useAdminTablePagination';
import {
  AcademicPanelCard,
  AcademicPanelListItem,
  AcademicPanelTableWrap,
} from './AcademicPanelCard';
import AcademiaSelect from '../../common/AcademiaSelect';

const PAGE_SIZE = ADMIN_TABLE_PAGE_SIZE;

const FIELD_LABEL = 'text-xs font-bold text-on-surface-variant';
const FIELD_LABEL_BLOCK = 'block min-w-0 text-sm';
const INPUT_CLASS =
  'w-full rounded-lg border border-outline-variant/30 bg-white py-2 text-sm text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

type ActivityRow = {
  id: string;
  when: string;
  categorie: string;
  categorieLabel: string;
  action: string;
  formateur: { id: string; label: string; specialite?: string | null };
  idSession?: string | null;
  detailsPreview?: string | null;
};

type CategoryOption = { id: string; label: string };

function formatShortDate(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

const TRACKED_ACTIVITY_TYPES = [
  { label: 'Profil pédagogique', icon: 'person' as const },
  { label: 'Documents justificatifs', icon: 'folder_shared' as const },
  { label: 'Feuilles de présences', icon: 'checklist' as const },
  { label: 'Notes & évaluations', icon: 'menu_book' as const },
  { label: 'Sessions clôturées', icon: 'event_available' as const },
];

const DETAIL_BTN_CLASS =
  'inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary transition hover:bg-primary/10';

function TablePaginationBar({
  total,
  currentPage,
  totalPages,
  onPageChange,
  summary,
}: {
  total: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  summary: string;
}) {
  if (total <= 0) return null;
  return (
    <AdminPagination
      className="w-full"
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      summary={summary}
    />
  );
}

export default function FormateurActivityJournalPanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ActivityRow[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [categorieFilter, setCategorieFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminFormateurActivities({
        page,
        limit: PAGE_SIZE,
        categorie: categorieFilter || undefined,
        q: search.trim() || undefined,
      });
      setItems(data?.items ?? []);
      setCategories(data?.categories ?? []);
      setTotalPages(data?.pagination?.totalPages ?? 1);
      setTotal(data?.pagination?.total ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le journal');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, categorieFilter, search]);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(load);

  useEffect(() => {
    setPage(1);
  }, [categorieFilter, search]);

  const summary = useMemo(() => {
    if (total === 0) return 'Aucune activité';
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, total);
    return `Affichage ${start}–${end} sur ${total} activité${total > 1 ? 's' : ''}`;
  }, [page, total]);

  return (
    <AdminPageShell
      title="Activités formateurs"
      description="Profil, documents, présences, notes et sessions — séparé du journal administratif."
      sideCard={{
        label: 'Activités enregistrées',
        value: loading && items.length === 0 ? '…' : String(total),
        helper: 'Actions réalisées par les formateurs sur la plateforme',
        sideLinks: [
          { label: 'Formateurs', to: '/admin/formateurs', icon: 'cast_for_education' },
          { label: 'Activités apprenants', to: '/admin/activites-apprenants', icon: 'history' },
        ],
      }}
      isLoading={loading && items.length === 0 && !error}
      isError={Boolean(error)}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement du journal…"
    >
      {error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
      ) : null}

      <div className="space-y-6">
        <section className="grid gap-4 lg:grid-cols-2">
          <AcademicPanelCard
            variant="success"
            icon={<LucideSymbolIcon name="checklist" tone="primary" size={22} />}
            title="Actions suivies"
            subtitle="Activités enregistrées pour le suivi pédagogique"
          >
            <ul className="academic-panel-list">
              {TRACKED_ACTIVITY_TYPES.map((item) => (
                <AcademicPanelListItem key={item.label}>
                  <span className="flex items-center gap-2 font-medium text-slate-900">
                    <LucideSymbolIcon name={item.icon} tone="primary" size={18} />
                    {item.label}
                  </span>
                </AcademicPanelListItem>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-relaxed text-on-surface-variant">
              Les actions admin sur les formateurs (validation, paiements) restent dans Sécurité & traçabilité.
            </p>
          </AcademicPanelCard>

          <AcademicPanelCard
            variant="accent"
            icon={<LucideSymbolIcon name="shield" tone="primary" size={22} />}
            title="Trois journaux distincts"
            subtitle="Apprenants, formateurs et administration"
            footer={
              <Link
                to="/admin/security"
                className="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-bold text-primary transition hover:bg-primary/10"
              >
                <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
                Ouvrir Sécurité & traçabilité
              </Link>
            }
          >
            <ul className="academic-panel-list">
              <AcademicPanelListItem>
                <span>
                  <span className="block font-medium text-slate-900">Journal formateurs</span>
                  <span className="text-xs text-on-surface-variant">Profil, documents, présences, notes</span>
                </span>
                <span className="academic-panel-badge academic-panel-badge--success">Cette page</span>
              </AcademicPanelListItem>
              <AcademicPanelListItem>
                <span>
                  <span className="block font-medium text-slate-900">Journal apprenants</span>
                  <span className="text-xs text-on-surface-variant">Inscriptions, paiements, dossiers</span>
                </span>
                <Link to="/admin/activites-apprenants" className="academic-panel-badge hover:underline">
                  Apprenants
                </Link>
              </AcademicPanelListItem>
              <AcademicPanelListItem>
                <span>
                  <span className="block font-medium text-slate-900">Journal administratif</span>
                  <span className="text-xs text-on-surface-variant">Validations, tarifs, logistique</span>
                </span>
                <span className="academic-panel-badge">Admin</span>
              </AcademicPanelListItem>
            </ul>
          </AcademicPanelCard>
        </section>

        <AcademicPanelCard
          flush
          icon={<LucideSymbolIcon name="history" tone="primary" size={22} />}
          title="Journal des activités"
          subtitle="Filtrable par catégorie et recherche formateur"
          footer={
            total > 0 ? (
              <TablePaginationBar
                total={total}
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                summary={summary}
              />
            ) : undefined
          }
        >
          <div className="academic-panel-toolbar">
            <div className="flex w-full flex-nowrap items-end gap-3">
              <label className={`${FIELD_LABEL_BLOCK} min-w-0 flex-1`}>
                <span className={FIELD_LABEL}>Rechercher</span>
                <div className="relative mt-1">
                  <LucideSymbolIcon
                    name="search"
                    tone="inherit"
                    size={18}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/70"
                  />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Nom, spécialité, e-mail, action…"
                    className={`${INPUT_CLASS} pl-10 pr-3`}
                    aria-label="Rechercher dans le journal"
                  />
                </div>
              </label>

              <label className={`${FIELD_LABEL_BLOCK} w-[10.5rem] shrink-0 sm:w-48`}>
                <span className={FIELD_LABEL}>Catégorie</span>
                <AcademiaSelect
                  fieldSize="sm"
                  className="mt-1 w-full"
                  value={categorieFilter}
                  onChange={(e) => setCategorieFilter(e.target.value)}
                  aria-label="Filtrer par catégorie"
                >
                  <option value="">Toutes</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </AcademiaSelect>
              </label>
            </div>
          </div>

          <AcademicPanelTableWrap
            empty={
              !loading && total === 0 ? (
                <p className="text-sm text-on-surface-variant">
                  Aucune activité enregistrée pour l&apos;instant. Les nouvelles actions formateur apparaîtront
                  ici automatiquement.
                </p>
              ) : undefined
            }
          >
            {loading && items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-on-surface-variant">Chargement…</p>
            ) : total > 0 ? (
              <table className="academic-panel-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Formateur</th>
                    <th>Catégorie</th>
                    <th>Activité</th>
                    <th className="hidden lg:table-cell">Aperçu</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-on-surface-variant">
                        Aucune activité sur cette page.
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap text-xs text-on-surface-variant">
                          {formatShortDate(row.when)}
                        </td>
                        <td>
                          <span className="font-semibold text-slate-900">{row.formateur.label}</span>
                          {row.formateur.specialite ? (
                            <p className="text-xs text-on-surface-variant">{row.formateur.specialite}</p>
                          ) : null}
                        </td>
                        <td>
                          <span className="academic-panel-badge">{row.categorieLabel}</span>
                        </td>
                        <td className="text-slate-800">{row.action}</td>
                        <td className="hidden max-w-xs truncate text-xs text-on-surface-variant lg:table-cell">
                          {row.detailsPreview ?? '—'}
                        </td>
                        <td className="text-right">
                          <Link
                            to={`/admin/activites-formateurs/${row.id}`}
                            state={{ activityLabel: row.action, formateurLabel: row.formateur.label }}
                            className={DETAIL_BTN_CLASS}
                            title="Voir le détail complet de l'activité"
                          >
                            <LucideSymbolIcon name="visibility" tone="inherit" size={16} />
                            Détails
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : null}
          </AcademicPanelTableWrap>
        </AcademicPanelCard>
      </div>
    </AdminPageShell>
  );
}
